import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { UserAvatar } from "@/components/UserAvatar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useAuthStore } from "@/store/authStore";
import {
  getUserDisplayName,
  getUserInitials,
  useOrgMembers,
  useTeamsStore,
} from "@/store/teamsStore";

interface OpportunityPersonFilterContentProps {
  selected: string[];
  counts?: Record<string, number>;
  onChange: (next: string[]) => void;
}

/**
 * Dropdown body for a single person filter (Sales Rep, Estimator, or Assignee):
 * a searchable multi-select of org users with per-user opportunity counts and an
 * "Only mine" shortcut that selects just the current user.
 * @param selected - Currently selected user ids for this role
 * @param counts - Per-user opportunity counts for this role
 * @param onChange - Called with the next selected user ids
 * @returns The person-filter dropdown content
 */
export function OpportunityPersonFilterContent({
  selected,
  counts,
  onChange,
}: OpportunityPersonFilterContentProps) {
  const users = useTeamsStore((state) => state.users);
  const usersById = useOrgMembers();
  const currentUserId = useAuthStore((state) => state.user?.id);
  const [commandValue, setCommandValue] = useState("");

  const currentUser = users.find((user) => user.id === currentUserId);
  const onlyMineActive = currentUserId
    ? selected.includes(currentUserId)
    : false;

  const toggle = (userId: string) => {
    const next = selected.includes(userId)
      ? selected.filter((id) => id !== userId)
      : [...selected, userId];
    onChange(next);
  };

  const toggleOnlyMine = (userId: string) => {
    onChange(onlyMineActive && selected.length === 1 ? [] : [userId]);
  };

  return (
    <div>
      {currentUser && currentUserId && (
        <div className="flex items-center justify-between px-3 pt-3">
          <Button
            variant={onlyMineActive ? "secondary" : "outline"}
            size="sm"
            className="h-7 gap-1.5 rounded-full px-2"
            onClick={() => toggleOnlyMine(currentUserId)}
          >
            <UserAvatar
              className="h-5 w-5"
              profilePictureUrl={
                usersById.get(currentUserId)?.profilePictureUrl
              }
              name={getUserDisplayName(currentUser)}
              initials={getUserInitials(currentUser)}
            />
            Only mine
          </Button>
          {selected.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-muted-foreground"
              onClick={() => onChange([])}
            >
              Clear
            </Button>
          )}
        </div>
      )}

      <Command
        className="mt-2"
        value={commandValue}
        onValueChange={setCommandValue}
      >
        <CommandInput placeholder="Search people..." />
        <CommandList onMouseLeave={() => setCommandValue("")}>
          <CommandEmpty>No people found.</CommandEmpty>
          <CommandGroup>
            {users.map((user) => {
              const isSelected = selected.includes(user.id);
              const count = counts?.[user.id];
              const profilePictureUrl = usersById.get(
                user.id,
              )?.profilePictureUrl;
              return (
                <CommandItem
                  key={user.id}
                  value={`${getUserDisplayName(user)} ${user.email}`}
                  onSelect={() => toggle(user.id)}
                  className="gap-2"
                >
                  <UserAvatar
                    className={
                      isSelected ? "h-7 w-7 ring-2 ring-primary" : "h-7 w-7"
                    }
                    profilePictureUrl={profilePictureUrl}
                    name={getUserDisplayName(user)}
                    initials={getUserInitials(user)}
                  />
                  <div className="flex flex-1 flex-col overflow-hidden">
                    <span className="truncate">{getUserDisplayName(user)}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {user.email}
                    </span>
                  </div>
                  {count ? (
                    <span className="text-sm text-muted-foreground">
                      {count}
                    </span>
                  ) : null}
                  <Checkbox
                    checked={isSelected}
                    className="pointer-events-none"
                  />
                </CommandItem>
              );
            })}
          </CommandGroup>
        </CommandList>
      </Command>
    </div>
  );
}
